import EffectComposer from '../utils/postprocessing/EffectComposer.js';
import RenderPass     from '../utils/postprocessing/RenderPass.js';
import ShaderPass     from '../utils/postprocessing/ShaderPass.js';
import MaskPass       from '../utils/postprocessing/MaskPass.js';
import CopyShader     from '../utils/postprocessing/shaders/CopyShader.js';
import FilmShader     from '../utils/postprocessing/shaders/FilmShader.js';
import RGBShiftShader from '../utils/postprocessing/shaders/RGBShiftShader.js';
import BadTVShader    from '../utils/postprocessing/shaders/BadTVShader.js';
import StaticShader   from '../utils/postprocessing/shaders/StaticShader.js';

const LOGO_WIDTH  = 188;
const LOGO_HEIGHT = 241;

export default class LogoScene {
  constructor(options) {
    const {
      rootEl,
    } = options;

    this.rootEl = rootEl;
    this.shaderTime = 0;
    this.isEnabled = true;

    this.init();
    // this.initGui();
    this.animate();
  }

  init() {
    const camera = new THREE.PerspectiveCamera(45, LOGO_WIDTH / LOGO_HEIGHT, 20, 3000);
    camera.position.z = 450;
    const scene = new THREE.Scene();
    this.camera = camera;
    this.scene = scene;
    //init video texture
    const videoTexture = new THREE.TextureLoader().load('assets/images/interactopus-black-bg.png');
    videoTexture.minFilter = THREE.LinearFilter;
    videoTexture.magFilter = THREE.LinearFilter;

    const videoMaterial = new THREE.MeshBasicMaterial( {
      map: videoTexture
    } );

    //Add video plane
    var planeGeometry = new THREE.PlaneGeometry( LOGO_WIDTH, LOGO_HEIGHT,1,1 );
    var plane = new THREE.Mesh( planeGeometry, videoMaterial );
    scene.add( plane );
    plane.z = 0;
    plane.scale.x = plane.scale.y = 1.45;

    //init renderer
    const renderer = new THREE.WebGLRenderer();
    renderer.setSize( LOGO_WIDTH, LOGO_HEIGHT);
    this.rootEl.appendChild( renderer.domElement );
    this.renderer = renderer;

    //POST PROCESSING
    //Create Shader Passes
    this.renderPass = new THREE.RenderPass( scene, camera );
    this.badTVPass = new THREE.ShaderPass( THREE.BadTVShader );
    this.rgbPass = new THREE.ShaderPass( THREE.RGBShiftShader );
    this.filmPass = new THREE.ShaderPass( THREE.FilmShader );
    this.staticPass = new THREE.ShaderPass( THREE.StaticShader );
    this.copyPass = new THREE.ShaderPass( THREE.CopyShader );

    //set shader uniforms
    this.filmPass.uniforms[ "grayscale" ].value = 0;

    //Init DAT GUI control panel
    this.badTVParams = {
      mute:true,
      show: true,
      distortion: 3.3,
      distortion2: 5.5,
      speed: 0.04,
      rollSpeed: 0.0
    }

    this.staticParams = {
      show: false,
      amount:0.5,
      size2:4.0
    }

    this.rgbParams = {
      show: true,
      amount: 0.005,
      angle: 0.0,
    }

    this.filmParams = {
      show: true,
      count: 222,
      sIntensity: 0.2,
      nIntensity: 1.6
    }


    this.onToggleShaders();
    // onToggleMute();
    this.onParamsChange();

    // window.addEventListener('resize', this.onResize, false);
    // this.onResize();
  }

  initGui() {
    const gui = new dat.GUI();
    const {
      badTVParams,
      rgbParams,
      staticParams,
      filmParams,
      renderPass,

      onToggleShaders,
      onParamsChange,
    } = this;

    // gui.add(badTVParams, 'mute').onChange(onToggleMute);

    var f1 = gui.addFolder('Bad TV');
    f1.add(badTVParams, 'show').onChange(onToggleShaders);
    f1.add(badTVParams, 'distortion', 0.1, 20).step(0.1).listen().name("Thick Distort").onChange(onParamsChange);
    f1.add(badTVParams, 'distortion2', 0.1, 20).step(0.1).listen().name("Fine Distort").onChange(onParamsChange);
    f1.add(badTVParams, 'speed', 0.0,1.0).step(0.01).listen().name("Distort Speed").onChange(onParamsChange);
    f1.add(badTVParams, 'rollSpeed', 0.0,1.0).step(0.01).listen().name("Roll Speed").onChange(onParamsChange);
    f1.open();

    var f2 = gui.addFolder('RGB Shift');
    f2.add(rgbParams, 'show').onChange(onToggleShaders);
    f2.add(rgbParams, 'amount', 0.0, 0.1).listen().onChange(onParamsChange);
    f2.add(rgbParams, 'angle', 0.0, 2.0).listen().onChange(onParamsChange);
    f2.open();

    var f4 = gui.addFolder('Static');
    f4.add(staticParams, 'show').onChange(onToggleShaders);
    f4.add(staticParams, 'amount', 0.0,1.0).step(0.01).listen().onChange(onParamsChange);
    f4.add(staticParams, 'size2', 1.0,100.0).step(1.0).onChange(onParamsChange);
    f4.open();

    var f3 = gui.addFolder('Scanlines');
    f3.add(filmParams, 'show').onChange(onToggleShaders);
    f3.add(filmParams, 'count', 50, 1000).onChange(onParamsChange);
    f3.add(filmParams, 'sIntensity', 0.0, 2.0).step(0.1).onChange(onParamsChange);
    f3.add(filmParams, 'nIntensity', 0.0, 2.0).step(0.1).onChange(onParamsChange);
    f3.open();

    gui.close();
  }

  onParamsChange = () => {
    const {
      badTVPass,
      staticPass,
      rgbPass,
      filmPass,

      badTVParams,
      rgbParams,
      staticParams,
      filmParams,
    } = this;
    //copy gui params into shader uniforms
    badTVPass.uniforms[ "distortion" ].value = badTVParams.distortion;
    badTVPass.uniforms[ "distortion2" ].value = badTVParams.distortion2;
    badTVPass.uniforms[ "speed" ].value = badTVParams.speed;
    badTVPass.uniforms[ "rollSpeed" ].value = badTVParams.rollSpeed;

    staticPass.uniforms[ "amount" ].value = staticParams.amount;
    staticPass.uniforms[ "size" ].value = staticParams.size2;

    rgbPass.uniforms[ "angle" ].value = rgbParams.angle*Math.PI;
    rgbPass.uniforms[ "amount" ].value = rgbParams.amount;

    filmPass.uniforms[ "sCount" ].value = filmParams.count;
    filmPass.uniforms[ "sIntensity" ].value = filmParams.sIntensity;
    filmPass.uniforms[ "nIntensity" ].value = filmParams.nIntensity;
  }

  onToggleShaders = () => {
    const {
      renderer,

      badTVPass,
      staticPass,
      rgbPass,
      filmPass,
      renderPass,
      copyPass,

      badTVParams,
      rgbParams,
      staticParams,
      filmParams,
    } = this;
    //Add Shader Passes to Composer
    //order is important
    const composer = new THREE.EffectComposer( renderer);
    composer.addPass( renderPass );

    if (filmParams.show){
      composer.addPass( filmPass );
    }

    if (badTVParams.show){
      composer.addPass( badTVPass );
    }

    if (rgbParams.show){
      composer.addPass( rgbPass );
    }

    if (staticParams.show){
      composer.addPass( staticPass );
    }

    composer.addPass( copyPass );
    copyPass.renderToScreen = true;


    this.composer = composer;
  }

  animate = () => {
    if (!this.isEnabled) {
      return;
    }
    const {
      badTVPass,
      staticPass,
      rgbPass,
      filmPass,
      renderPass,
      copyPass,
      animate,

      composer,

      renderer,
      scene,
      camera,
    } = this;
    this.shaderTime += 0.1;
    const shaderTime = this.shaderTime;
    badTVPass.uniforms[ 'time' ].value =  shaderTime;
    filmPass.uniforms[ 'time' ].value =  shaderTime;
    staticPass.uniforms[ 'time' ].value =  shaderTime;

    // if ( video.readyState === video.HAVE_ENOUGH_DATA ) {
    //   if ( videoTexture ) videoTexture.needsUpdate = true;
    // }

    requestAnimationFrame( animate );
    // renderer.render(scene, camera);
    composer.render( 0.1);
    // composer2.render( 0.1);
  }

  onResize = () => {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }

  disable() {
    this.isEnabled = false;
  }

  enable() {
    this.isEnabled  = true;
    this.animate();
  }
}



/** WEBPACK FOOTER **
 ** ./src/scenes/LogoScene.js
 **/